import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Download, Trash2, XCircle, CheckCircle, Clock, Undo2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AccountCancellation({ user, accountStatus, onStatusChange }) {
  const [showConfirm1, setShowConfirm1] = useState(false);
  const [showConfirm2, setShowConfirm2] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [exportingJson, setExportingJson] = useState(false);
  const [exportingZip, setExportingZip] = useState(false);

  const isPendingDeletion = accountStatus?.account_status === 'pending_deletion';
  const scheduledDate = accountStatus?.scheduled_deletion_date 
    ? new Date(accountStatus.scheduled_deletion_date) 
    : null;
  const daysUntilDeletion = accountStatus?.days_until_deletion;

  const handleExportData = async (format) => {
    const setExporting = format === 'json' ? setExportingJson : setExportingZip;
    setExporting(true);
    try {
      const response = await axios.get(`${API}/account/export-data?format=${format}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `export.${format}`;
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Data exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleFirstConfirm = () => {
    setShowConfirm1(false);
    setShowConfirm2(true);
    setConfirmText('');
  };

  const handleCancelAccount = async () => {
    if (confirmText !== 'DELETE MY ACCOUNT') {
      toast.error('Please type the confirmation text exactly');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/account/request-cancellation`);
      toast.success('Account cancellation scheduled');
      setShowConfirm2(false);
      setConfirmText('');
      if (onStatusChange) onStatusChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel account');
    } finally {
      setLoading(false);
    }
  };

  const handleUndoCancellation = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/account/cancel-deletion`);
      toast.success('Account deletion cancelled');
      if (onStatusChange) onStatusChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel deletion');
    } finally {
      setLoading(false);
    }
  };

  // Platform admin can export data but not cancel account
  const isPlatformAdmin = user?.is_platform_admin;

  return (
    <div className="space-y-6" data-testid="account-cancellation-section">
      {/* Export Data Section - Always show */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-neon-blue" />
            Export Your Data
          </CardTitle>
          <CardDescription>
            Download a complete backup of all your company data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => handleExportData('json')}
              disabled={exportingJson}
              className="flex-1 border-border"
              data-testid="export-json-settings-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exportingJson ? 'Exporting...' : 'Export as JSON'}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExportData('zip')}
              disabled={exportingZip}
              className="flex-1 border-border"
              data-testid="export-zip-settings-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exportingZip ? 'Exporting...' : 'Export as ZIP (CSV files)'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            JSON format contains all data in a single file. ZIP contains separate CSV files for each data type plus a full JSON backup.
          </p>
        </CardContent>
      </Card>

      {/* Pending Deletion Status - Only for non-platform-admin */}
      {!isPlatformAdmin && isPendingDeletion && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <Clock className="w-5 h-5" />
              Account Deletion Scheduled
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <p className="text-muted-foreground">
                Your account and all data will be permanently deleted on:
              </p>
              <p className="text-xl font-bold text-red-400">
                {scheduledDate?.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
              <p className="text-sm text-muted-foreground">
                {daysUntilDeletion} days remaining to change your mind
              </p>
            </div>
            
            <Button
              onClick={handleUndoCancellation}
              disabled={loading}
              className="w-full bg-green-500 text-white hover:bg-green-600"
              data-testid="undo-cancellation-btn"
            >
              <Undo2 className="w-4 h-4 mr-2" />
              {loading ? 'Processing...' : 'Keep My Account'}
            </Button>
            
            <p className="text-xs text-center text-muted-foreground">
              Click above to cancel the deletion and keep your account active
            </p>
          </CardContent>
        </Card>
      )}

      {/* Cancel Account Section - Only for non-platform-admin */}
      {!isPlatformAdmin && !isPendingDeletion && (
        <Card className="bg-card border-red-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Cancel Account
            </CardTitle>
            <CardDescription>
              Permanently delete your account and all associated data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-red-500/10 rounded-lg p-4 mb-4">
              <p className="text-sm text-muted-foreground">
                <strong className="text-red-400">Warning:</strong> This action will schedule your account for deletion in 7 days. 
                All your data including bookings, leads, clients, contracts, invoices, and settings will be permanently removed.
                You can undo this action within the 7-day period.
              </p>
            </div>
            
            <Button
              variant="destructive"
              onClick={() => setShowConfirm1(true)}
              className="w-full bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
              data-testid="cancel-account-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancel My Account
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Platform admin notice */}
      {isPlatformAdmin && (
        <Card className="bg-muted/20 border-border">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              As the platform administrator, your account cannot be cancelled through this interface. 
              Contact system support if you need to transfer ownership or close the platform.
            </p>
          </CardContent>
        </Card>
      )}

      {/* First Confirmation Dialog */}
      <Dialog open={showConfirm1} onOpenChange={setShowConfirm1}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Are you sure?
            </DialogTitle>
            <DialogDescription>
              This is the first step to cancel your account.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-muted-foreground">
              Before cancelling, we recommend downloading your data:
            </p>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleExportData('json')}
                disabled={exportingJson}
                className="flex-1 border-border"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportingJson ? 'Exporting...' : 'Export JSON'}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExportData('zip')}
                disabled={exportingZip}
                className="flex-1 border-border"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportingZip ? 'Exporting...' : 'Export ZIP'}
              </Button>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-sm text-yellow-400">
                <strong>Important:</strong> Once deleted, your data cannot be recovered. 
                Make sure to download your data before proceeding.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm1(false)} className="border-border">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleFirstConfirm}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              I Understand, Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Second Confirmation Dialog */}
      <Dialog open={showConfirm2} onOpenChange={setShowConfirm2}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Final Confirmation
            </DialogTitle>
            <DialogDescription>
              This is your final confirmation before scheduling deletion.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-sm text-red-400 font-medium mb-2">
                This will permanently delete:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>All bookings and leads</li>
                <li>All clients and contacts</li>
                <li>All contracts and invoices</li>
                <li>All employees and packages</li>
                <li>All forms, templates, and documents</li>
                <li>All settings and preferences</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label>Type <strong className="text-red-400">DELETE MY ACCOUNT</strong> to confirm:</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE MY ACCOUNT"
                className="bg-muted/20 border-border"
                data-testid="delete-confirmation-input"
              />
            </div>
            
            <p className="text-xs text-muted-foreground">
              After confirmation, you'll have 7 days to change your mind before the deletion becomes permanent.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm2(false)} className="border-border">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelAccount}
              disabled={loading || confirmText !== 'DELETE MY ACCOUNT'}
              className="bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              data-testid="final-delete-btn"
            >
              {loading ? 'Processing...' : 'Schedule Account Deletion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
